import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';
import { track, identifyUser, clearUserIdentity, initAnalytics, isAdminEmail } from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analyticsEvents';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName?: string, lastName?: string, phone?: string) => Promise<void>;
  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string }) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
  isAdmin: () => boolean;
  /** Refetch current user from API (e.g. after tour finished so user has latest onboarding_tour_finished_at). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Decode and validate JWT token
 * Returns true if token is valid and not expired, false otherwise
 */
const isTokenValid = (token: string): boolean => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decodedPayload = JSON.parse(
      atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='))
    );

    // Check expiration (exp is in seconds, Date.now() is in milliseconds)
    if (decodedPayload.exp) {
      const expirationTime = decodedPayload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      // Add a small buffer (5 minutes) to account for clock skew
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      if (currentTime >= expirationTime - bufferTime) {
        // Token is expired or about to expire
        return false;
      }
    }

    return true;
  } catch (error) {
    // If we can't decode the token, it's invalid
    return false;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Keep the analytics admin flag in sync with user state.
  // Parent effects run before child effects, so RouteTracker (in App.tsx)
  // will always see the correct _isAdmin value by the time its effect fires.
  // Only initialize analytics after loading completes to avoid initializing
  // Meta Pixel before we know if the user is an admin.
  useEffect(() => {
    if (!loading) {
      initAnalytics(isAdminEmail(user?.email) || Boolean(user?.exclude_from_analytics));
      if (user?.id) {
        identifyUser(user.id);
      }
    }
  }, [user, loading]);

  // When any API returns 401, clear user so ProtectedRoute redirects to login
  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token) {
      // First check if token is valid (not expired) before making API call
      if (!isTokenValid(token)) {
        localStorage.removeItem('token');
        setLoading(false);
        return;
      }

      const maxRetries = 6;
      let attempts = 0;
      const getRetryDelayMs = (attempt: number) => Math.min(1000 * (2 ** attempt), 8000);

      const fetchUser = (): Promise<void> =>
        authAPI.getCurrentUser()
          .then((userData) => {
            setUser(userData);
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              localStorage.removeItem('token');
              return;
            }
            const status = error.response?.status;
            const isTransient = !error.response || status >= 500 || status === 429;
            // Retry transient backend/network failures with exponential backoff.
            if (isTransient && attempts < maxRetries) {
              const delayMs = getRetryDelayMs(attempts);
              attempts += 1;
              return new Promise<void>((resolve) => setTimeout(resolve, delayMs)).then(fetchUser);
            }
          })
          .finally(() => {
            setLoading(false);
          });

      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('token', response.access_token);
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      initAnalytics(isAdminEmail(userData.email) || Boolean(userData.exclude_from_analytics));
      identifyUser(userData.id);
      track(AnalyticsEvents.LOGIN, { method: 'email' });
    } catch (error: any) {
      // Gate failed-login tracking by the email being attempted
      if (!isAdminEmail(email)) {
        initAnalytics(false);
        track(AnalyticsEvents.LOGIN_FAILED, { reason: error.response?.status === 403 ? 'inactive' : 'credentials' });
      }
      if (error.response?.status === 403) {
        throw new Error('Your account is inactive. Please contact support.');
      }
      throw error;
    }
  };

  const signup = async (email: string, password: string, firstName?: string, lastName?: string, phone?: string) => {
    try {
      if (!phone || !phone.trim()) {
        throw new Error('Phone number is required');
      }
      const response = await authAPI.signup({ email, password, first_name: firstName, last_name: lastName, phone: phone.trim() });
      localStorage.setItem('token', response.access_token);
      sessionStorage.setItem('cold_caller_show_tour', '1'); // before setUser so tour shows after redirect
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      initAnalytics(isAdminEmail(userData.email) || Boolean(userData.exclude_from_analytics));
      identifyUser(userData.id);
      track(AnalyticsEvents.SIGNUP_COMPLETE, { method: 'email' });
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (data: { first_name?: string; last_name?: string; phone?: string }) => {
    await authAPI.updateProfile(data);
    const userData = await authAPI.getCurrentUser();
    setUser(userData);
  };

  const logout = () => {
    const wasAdmin = isAdminEmail(user?.email);
    if (!wasAdmin) {
      track(AnalyticsEvents.LOGOUT);
    }
    clearUserIdentity();
    localStorage.removeItem('token');
    sessionStorage.clear();
    // CCT-8: purge every per-user namespace, not just `cold_caller_*`. Left
    // behind, the campaign-wizard draft, onboarding checklist, command-palette
    // recents, sidebar visit counts, and settings recents leak the prior
    // user's content / navigation history to the next user on a shared
    // workstation. Match any key under a known app-owned prefix.
    const PURGE_PREFIXES = [
      'cold_caller_',
      'cc_',
      'onboardingChecklist:',
      'coldcaller:',
      'commandPalette:',
      'settings.',
    ];
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && PURGE_PREFIXES.some((p) => key.startsWith(p))) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    setUser(null);
    initAnalytics(false);
  };

  const resetPassword = async (email: string) => {
    try {
      await authAPI.resetPasswordRequest({ email });
    } catch (error) {
      throw error;
    }
  };

  const isAdmin = () => isAdminEmail(user?.email);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch {
      // Ignore; token may be invalid
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    updateProfile,
    logout,
    resetPassword,
    isAdmin,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
